// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react';
import {
  Box,
  ExpandableSection,
  SpaceBetween,
  TextContent,
} from '@cloudscape-design/components';
import CopyableCodeView from '../CopyableCodeView';
import {
  cliOnboardCode,
  cliOnboardProfileCode,
  cliOnboardCustomNameCode,
  cliAdaProfileCode,
} from '../constants';

const CliOnboardStep = () => {
  return (
    <SpaceBetween size="m">
      <Box>
        <TextContent>
          <p>
            Onboard your account by providing the account ID directly. The CLI
            will use ADA behind the scenes to obtain credentials and scan all
            enabled regions:
          </p>
        </TextContent>
        <CopyableCodeView content={cliOnboardCode} />
      </Box>

      <ExpandableSection headerText="Credential options">
        <TextContent>
          <p>
            By default, this uses Conduit with the{' '}
            <code>IibsAdminAccess-DO-NOT-DELETE</code> role. You can customize
            with these flags:
          </p>
          <ul>
            <li>
              <code>--role &lt;role&gt;</code> — Use a different IAM role
            </li>
            <li>
              <code>--provider conduit|isengard</code> — Use Isengard instead of
              Conduit
            </li>
            <li>
              <code>--conduit-read-only</code> — Use read-only Conduit
              credentials
            </li>
            <li>
              <code>--conduit-view-only</code> — Use view-only Conduit
              credentials
            </li>
          </ul>
        </TextContent>
      </ExpandableSection>

      <ExpandableSection headerText="Prefer using an ADA profile?">
        <SpaceBetween size="s">
          <TextContent>
            <p>
              If you have an existing ADA profile configured, use the{' '}
              <code>--profile</code> flag instead:
            </p>
          </TextContent>
          <CopyableCodeView content={cliOnboardProfileCode} />
          <TextContent>
            <p>
              To create a new profile, run:
            </p>
          </TextContent>
          <CopyableCodeView content={cliAdaProfileCode} />
          <TextContent>
            <ul>
              <li>
                Replace <code>&lt;ACCOUNT_ID&gt;</code> with your AWS account ID
                and <code>&lt;PROFILE_NAME&gt;</code> with a friendly name.
              </li>
              <li>
                We recommend the{' '}
                <code>IibsAdminAccess-DO-NOT-DELETE</code> role for complete
                resource discovery.
              </li>
            </ul>
          </TextContent>
        </SpaceBetween>
      </ExpandableSection>

      <ExpandableSection headerText="Custom display name & other options">
        <SpaceBetween size="s">
          <TextContent>
            <p>
              Set a custom display name for your account in ResourceLens with{' '}
              <code>--name</code>:
            </p>
          </TextContent>
          <CopyableCodeView content={cliOnboardCustomNameCode} />
          <TextContent>
            <ul>
              <li>
                If not specified, the display name defaults to the account ID
                (when using <code>--account</code>) or the profile name (when
                using <code>--profile</code>).
              </li>
              <li>
                Make sure you&apos;re authenticated with <code>mwinit</code>{' '}
                before running the onboard command.
              </li>
              <li>
                Optionally use <code>-r us-east-1,us-west-2</code> to limit to
                specific regions. Omitting <code>-r</code> scans all enabled
                regions.
              </li>
            </ul>
          </TextContent>
        </SpaceBetween>
      </ExpandableSection>
    </SpaceBetween>
  );
};

export default CliOnboardStep;
